<!-- PROVISÓRIO (R2-X-4 = B, 7-9): the kit's StatTile plus the woot's variation (arrow and colour) until the kit has one.
     Same markup as the shared `cards/StatTile.vue` (a shared piece, not touched here), with one line more: the change
     against the previous period, up or down, green when it is good and red when it is bad (for times, going up is bad:
     `lowerIsBetter`). The arrow and the colour are not the only signal: the words go to screen readers. A report without
     variation uses the shared StatTile. Swap for the kit's tile when it shows variation. -->
<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { CaretDown16, CaretUp16, Information16 } from '@carbon/icons-vue';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Skeleton } from '../ui/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';

const props = defineProps({
  label: { type: String, required: true },
  value: { type: String, default: '-' }, // already formatted: "1,204", "92%", "3m 20s"
  hint: { type: String, default: '' },
  info: { type: String, default: '' },
  loading: Boolean,
  trend: { type: Number, default: 0 }, // % against the previous period; 0 shows nothing (as the woot)
  lowerIsBetter: Boolean,
});
const { t } = useI18n();
const good = computed(() =>
  props.lowerIsBetter ? props.trend < 0 : props.trend > 0
);
const amount = computed(() => Math.abs(props.trend).toLocaleString());
</script>

<template>
  <Card class="gap-0 py-4" data-stat-tile :aria-busy="loading || undefined">
    <CardContent class="grid gap-1 px-4">
      <p
        class="flex items-center gap-1 type-polaris-caption text-polaris-muted-foreground"
      >
        {{ label }}
        <Tooltip v-if="info">
          <TooltipTrigger as-child>
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              class="size-6 text-polaris-muted-foreground"
              :aria-label="t('POLARIS.STAT_TILE.ABOUT', { label })"
            >
              <Information16 aria-hidden="true" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>{{ info }}</TooltipContent>
        </Tooltip>
      </p>
      <Skeleton v-if="loading" class="h-9 w-20" />
      <div v-else class="flex min-w-0 flex-wrap items-baseline gap-x-2">
        <p class="min-w-0 break-words type-polaris-display">{{ value }}</p>
        <p
          v-if="trend"
          class="flex items-center gap-0.5 type-polaris-caption font-medium"
          :class="
            good
              ? 'text-polaris-success-foreground'
              : 'text-polaris-destructive'
          "
          data-trend
          :data-good="good"
        >
          <CaretUp16 v-if="trend > 0" aria-hidden="true" />
          <CaretDown16 v-else aria-hidden="true" />
          <span aria-hidden="true">
            {{ t('POLARIS_REPORTS.TREND.SHORT', { value: amount }) }}
          </span>
          <span class="sr-only">
            {{
              trend > 0
                ? t('POLARIS_REPORTS.TREND.UP', { value: amount })
                : t('POLARIS_REPORTS.TREND.DOWN', { value: amount })
            }}
          </span>
        </p>
      </div>
      <p v-if="hint" class="type-polaris-caption text-polaris-muted-foreground">
        {{ hint }}
      </p>
    </CardContent>
  </Card>
</template>
